import React, { useCallback, useState } from 'react';
import { HiOutlineEye } from 'react-icons/hi2';
import { MdOutlineModeEditOutline } from 'react-icons/md';
import { Link } from 'react-router-dom';
import { ActionIcon, Button, Input, Switch, Text, Tooltip } from 'rizzui';
import { toast } from 'sonner';
import DeletePopover from '../../../components/buttons/DeletePopover';
import { usePrivilege } from '../../../components/priviledge/PriviledgeProvider';
import { useFiltersPR } from '../../../components/providers/FiltersProvider';
import { FilterDrawerView } from '../../../components/table/components/TableFilter';
import CustomTable from '../../../components/table/CustomTable';
import useFetch from '../../../hooks/useFetch';
import useHandleErrors from '../../../hooks/useHandleErrors';
import Page from '../../../layout/Page/Page';
import { menuRoutes } from '../../../router/menu';
import { ClientService } from '../../../services/client/clientService';
import { ClientsApiResponse } from '../../../type/entities/client-type';
import ClientsFilters from './ClientsFilters';
import { RiMapPinAddLine } from "react-icons/ri";
import AddParksToClientsModal from './components/AddParksToClientsModal';


interface ClientsListProps { }

const columnOrder = [
    'id',
    'company',
    'name',
    'contactName',
    'contactEmail',
    'contactPhone',
    'parkHasClients',
    'status',
];

const ClientsList: React.FC<ClientsListProps> = ({ }) => {

    // STATES

    const [changingStatus, setChangingStatus] = useState<string[]>([]);
    const [openFilters, setOpenFilters] = useState<boolean>(false);
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [client, setClient] = useState<any>(null);
    const [parkHasClients, setParkHasClients] = useState<any>([]);

    // HOOKS

    const { handleErrors } = useHandleErrors();
    const { filters, updateFilters, updateFilterOrder, updatePage, updatePageSize, resetFilters } = useFiltersPR();
    const service = new ClientService();
    const { userCan } = usePrivilege();

    // FUNCTIONS

    // -----------------------------------------------------------------------------------------------------------------
    /**
     * @ES OBTENER LISTA DE CLIENTES
     * @EN GET CLIENTS LIST
     */
    // -----------------------------------------------------------------------------------------------------------------
    const [data, loading, error, refetch] = useFetch(useCallback(async () => {
        const response = await service.listClients(filters);
        return response.getResponseData() as ClientsApiResponse;
    }, [filters]));
    // -----------------------------------------------------------------------------------------------------------------

    // -----------------------------------------------------------------------------------------------------------------
    /**
     * @ES ACTUALIZAR EL ESTADO DEL CLIENTE
     * @EN UPDATE CLIENT STATUS
     */
    // -----------------------------------------------------------------------------------------------------------------
    const toggleState = async (id: string, status: boolean) => {
        try {
            setChangingStatus([...changingStatus, id]);
            const response = (await service.toggleClientState(id, status)).getResponseData();
            if (response.success) {
                setChangingStatus(changingStatus.filter((item) => item !== id));
                refetch();
                toast.success(status ? "Se ha desactivado el cliente" : "Se ha activado el cliente");
            } else {
                setChangingStatus(changingStatus.filter((item) => item !== id));
                toast.error(response.message);
            }
        } catch (error: any) {
            setChangingStatus(changingStatus.filter((item) => item !== id));
            toast.error(error.message);
        }
    };
    // -----------------------------------------------------------------------------------------------------------------

    // -----------------------------------------------------------------------------------------------------------------
    /**
     * @ES MANEJO DEL BORRADO DE CLIENTES
     * @EN HANDLER FOR CLIENTS DELETION
     */
    // -----------------------------------------------------------------------------------------------------------------
    const handleDelete = async (id: string) => {
        const response = await (await service.deleteClient(id)).getResponseData();
        if (response.success) {
            toast.success('Cliente eliminado correctamente');
            refetch();
        } else {
            handleErrors(response);
        }
    };
    // -----------------------------------------------------------------------------------------------------------------

    // -----------------------------------------------------------------------------------------------------------------
    /**
     * @ES MANEJO DEL BORRADO MULTIPLE DE CLIENTES
     * @EN HANDLER FOR MULTIPLE CLIENTS DELETION
     */
    // -----------------------------------------------------------------------------------------------------------------
    const handleMultiDelete = async (ids: string[]) => {
        const response = await (await service.deleteMultiClients(ids)).getResponseData();
        if (response.success) {
            toast.success('Clientes eliminados correctamente');
            refetch();
        } else {
            handleErrors(response);
        }
    };
    // -----------------------------------------------------------------------------------------------------------------

    // -----------------------------------------------------------------------------------------------------------------
    /**
     * @ES MANEJA LA ORDENACION DE LAS COLUMNAS
     * @EN HANDLE COLUMN ORDER
     */ 
    // -----------------------------------------------------------------------------------------------------------------
    const orderedData = () => data?.data.map((row: any) => {
        const orderedRow: any = {};
        columnOrder.forEach((key) => {
            orderedRow[key] = row[key];
        });
        return orderedRow;
    });
    // -----------------------------------------------------------------------------------------------------------------

    // -----------------------------------------------------------------------------------------------------------------
    /**
     * @ES MANEJO DE CIERRE DEL MODAL
     * @EN HANDLER FOR CLOSING THE MODAL
     */
    // -----------------------------------------------------------------------------------------------------------------
    const onClose = () => {
        setIsOpen(false);
        setClient(null);
        setParkHasClients([]);
    }
    // -----------------------------------------------------------------------------------------------------------------

    // RENDER

    return (
        <Page container='fluid'>
            {data !== undefined && (
                <CustomTable
                    id={'clients-table'}
                    divClassName='max-h-table'
                    classNameTable='fs-table'
                    headerTableClassName='fs-table-header'
                    columnOrder={columnOrder}
                    data={orderedData()}
                    isLoading={loading}
                    isSortable
                    columnsNoSortables={['status', 'parkHasClients']}
                    columnsNotShown={[]}
                    overrideColumns={[
                        {
                            key: 'name',
                            render: (row: any) => {
                                const clientName = row.row.original.name;
                                
                                if (userCan('get', 'clients')) {
                                    return (
                                        <a href={`${menuRoutes.clients.path}/${row.row.original.id}/profile/${menuRoutes.clients.profile.info}`} className='text-primary hover:text-primary hover:underline'>
                                            {clientName}
                                        </a>
                                    );
                                }
                                
                                return <span>{clientName}</span>;
                            }
                        },
                        {
                            key: 'company',
                            permissions: [{ group: 'companies', permission: 'list' }],
                            render: (row: any) => {
                                return row.row.original.company.name
                            },
                        },
                        {
                            key: 'status',
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={row.row.original.status ? 'Desactivar cliente' : 'Activar cliente'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <Switch
                                            id={row.row.original.id}
                                            checked={row.row.original.status}
                                            disabled={!userCan('edit', 'clients')}
                                            onChange={() => toggleState(row.row.original.id, row.row.original.status)}
                                            switchKnobClassName='bg-red-300'
                                        />
                                    </Tooltip>
                                );
                            },
                        },
                        {
                            key: 'parkHasClients',
                            render: (row: any) => {
                                return row.row.original.parkHasClients.length > 0 ? (
                                        <span>{row.row.original.parkHasClients.map((park: any) => park.park.name).join(', ')}</span>
                                ) : (
                                       <span> Sin parque asignado</span>
                                );
                            },
                        }
                    ]}
                    actions={[
                        {
                            label: 'View',
                            permissions: { group: 'clients', permission: 'get' },
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Ver perfil'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <Link
                                            to={`${menuRoutes.clients.path}/${row.id}/profile/${menuRoutes.clients.profile.info}`}>
                                            <ActionIcon
                                                as="span"
                                                size="sm"
                                                variant="outline"
                                                className="hover:!border-gray-900 hover:text-gray-700 bg-white cursor-pointer w-5 h-5"
                                            >
                                                <HiOutlineEye className="h-3 w-3" size={20} />
                                            </ActionIcon>
                                        </Link>
                                    </Tooltip>
                                );
                            },
                        },
                        {
                            label: 'Edit',
                            permissions: { group: 'clients', permission: 'edit' },
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Editar cliente'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <Link to={`${menuRoutes.clients.path}/${row.id}/edit`}>
                                            <ActionIcon
                                                as="span"
                                                size="sm"
                                                variant="outline"
                                                className="hover:!border-gray-900 hover:text-gray-700 bg-white cursor-pointer w-5 h-5"
                                            >
                                                <MdOutlineModeEditOutline className="h-3 w-3" size={20} />
                                            </ActionIcon>
                                        </Link>
                                    </Tooltip>
                                );
                            },
                        },
                        {
                            label: 'Add',
                            permissions: { group: 'park', permission: 'assign_client_to_park' },
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Agregar parques'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <ActionIcon
                                            as="span"
                                            size="sm"
                                            variant="outline"
                                            className="hover:!border-gray-900 hover:text-gray-700 bg-white cursor-pointer w-5 h-5"
                                            onClick={() => {
                                                setClient(row);
                                                setParkHasClients(row.parkHasClients);
                                                setIsOpen(true);
                                            }
                                        }
                                        >
                                            <RiMapPinAddLine className="h-3 w-3" size={20} />
                                        </ActionIcon>
                                    </Tooltip>
                                );
                            },
                        },
                        {
                            label: 'Delete',
                            permissions: { group: 'clients', permission: 'delete' },
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Eliminar cliente'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <div>
                                            <DeletePopover
                                                actionIconClassName='w-5 h-5'
                                                title={`Eliminar cliente`}
                                                description={`¿Estás seguro de que deseas eliminar a ${row.name}?`}
                                                onDelete={() => handleDelete(row.id)}
                                            />
                                        </div>
                                    </Tooltip>
                                );
                            },
                        },
                    ]}
                    handleMultipleDelete={handleMultiDelete}
                    filters={filters}
                    updateFilters={updateFilters}
                    updateFilterOrder={updateFilterOrder}
                    defaultOrder={filters.filter_order || undefined}
                    paginationData={{
                        pageSize: filters.limit,
                        currentPage: filters.page,
                        pageCount: (data as ClientsApiResponse) ? data.lastPage : 1,
                        totalCount: data?.totalRegisters,
                        handlePagination: updatePage,
                        handlePerPage: updatePageSize,
                    }}
                    toggleFilters={() => setOpenFilters(!openFilters)}
                />
            )}



            <FilterDrawerView isOpen={openFilters} setOpenDrawer={setOpenFilters} drawerTitle={'Filtros Clientes'}>
                <ClientsFilters filters={filters} updateFilters={updateFilters} resetFilters={resetFilters} />
            </FilterDrawerView>

            <AddParksToClientsModal isOpen={isOpen} refetch={refetch} onClose={onClose} client={client} parkHasClients={parkHasClients} />

        </Page>
    );
};

export default ClientsList;